import React, { useState, useMemo, useRef, useEffect } from 'react';
import { GenericForm, GenericFormField, firstMissingRequiredMessage } from '@/styles/GenericForm';
import { useUser } from '@/contexts/UserContext';
import { trackTimeEntryCreated, trackTimeEntryUpdated, trackFormSubmission } from '@/lib/posthog-events';
import { useProject, getDefaultProjectIdForNewRecord } from '@/components/ProjectSelector';
import { localDateString, localTimeString, parseDurationMinutes, durationFieldValue, composeEntryTimes } from '@/lib/time-entry-time';
import { createTimeEntry, updateTimeEntry } from '../api';

interface TimeEntryFormProps {
  mode: 'create' | 'edit';
  initialData?: {
    id?: string;
    project_id?: string;
    /** Exact start instant (ISO). */
    start_time?: string;
    /** Exact duration in whole seconds (end - start). */
    duration_seconds?: number;
  } | null;
  projects: { id: string; name: string }[];
  /** Called right after a successful save (the form is already closing). */
  onSaved: () => void;
  onCancel: () => void;
  isOpen: boolean;
}

export function TimeEntryForm({ mode, initialData, projects, onSaved, onCancel, isOpen }: TimeEntryFormProps) {
  const { selectedProject } = useProject();
  const { companyId, user } = useUser();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const previousIsOpenRef = useRef<boolean>(false);

  const initialValues = useMemo(() => {
    if (mode === 'edit' && initialData?.start_time) {
      const start = new Date(initialData.start_time);
      return {
        project_id: initialData.project_id || '',
        // Local date/time of the start, the same frame the user edits in (never the UTC date).
        date: localDateString(start),
        start_time: localTimeString(start),
        duration: durationFieldValue(initialData.duration_seconds ?? 0),
      };
    }
    const now = new Date();
    return {
      project_id: getDefaultProjectIdForNewRecord(selectedProject),
      date: localDateString(now),
      start_time: localTimeString(now),
      duration: '',
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, initialData?.id, initialData?.start_time, initialData?.duration_seconds, initialData?.project_id, selectedProject, isOpen]);

  useEffect(() => {
    if (isOpen && !previousIsOpenRef.current) setError(null);
    previousIsOpenRef.current = isOpen;
  }, [isOpen]);

  const fields = useMemo<GenericFormField[]>(() => [
    { name: 'project_id', label: 'Project', type: 'select', required: true, row: 1, colSpan: 1, options: projects.map((p) => ({ label: p.name, value: p.id })), placeholder: 'Select' },
    { name: 'date', label: 'Date', type: 'date', required: true, row: 1, colSpan: 1 },
    { name: 'start_time', label: 'Start Time', type: 'time', required: true, row: 2, colSpan: 1 },
    { name: 'duration', label: 'Duration', type: 'text', required: true, row: 2, colSpan: 1, placeholder: '00:00' },
  ], [projects]);

  const handleSubmit = async (data: Record<string, any>) => {
    if (isSubmitting) return;
    setError(null);
    const missing = firstMissingRequiredMessage(fields, data);
    if (missing) { setError(missing); return; }

    const minutes = parseDurationMinutes(String(data.duration));
    if (!minutes || minutes <= 0) { setError('Please enter a duration greater than 0'); return; }

    setIsSubmitting(true);
    try {
      // Exact instants from what the user typed: start (local) + the typed minutes. No decimal hours.
      const { startISO, endISO } = composeEntryTimes(data.date, data.start_time, minutes);
      const input = { projectId: data.project_id, date: data.date, startISO, endISO };
      const analytics = { project_id: data.project_id, duration_hours: minutes / 60, date: data.date, start_time: startISO, end_time: endISO, company_id: companyId };

      if (mode === 'edit' && initialData?.id) {
        await updateTimeEntry(initialData.id, input);
        trackTimeEntryUpdated({ time_entry_id: initialData.id, ...analytics, updated_by: user?.id });
      } else {
        const created = await createTimeEntry(input);
        trackTimeEntryCreated({ time_entry_id: created.id, ...analytics, task_type: 'manual_entry', billable: true, created_by: user?.id });
      }
      trackFormSubmission({ form_type: 'time_entry_creation', success: true });

      // The entry is saved: close right away. The list refreshes in the background.
      onCancel();
      onSaved();
    } catch (err: any) {
      console.error('Error submitting time entry:', err);
      setError(err?.message || 'Failed to save time entry');
      trackFormSubmission({ form_type: 'time_entry_creation', success: false, error_message: err?.message || 'Failed to save time entry' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <GenericForm
      title="Time Entry"
      mode={mode}
      fields={fields}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      isOpen={isOpen}
      error={error}
      submitButtonText="Save Changes"
      saving={isSubmitting}
      size="compact"
    />
  );
}
